import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "panel", "frame"]
  static values = { open: Boolean, locationResolved: Boolean }

  connect() {
    this.boundKey = this.handleKey.bind(this)
    document.addEventListener("keydown", this.boundKey)
    // Always start closed — Turbo cache can otherwise restore a previously open state.
    this.openValue = false
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    document.body.style.overflow = ""

    // If the user was trying to open a product modal before picking a
    // location, gate() stashed that URL in sessionStorage and sent them
    // through the location flow first. Now that we're back on the page,
    // open the pending product modal by setting the turbo frame src.
    const pending = sessionStorage.getItem("pending_product_modal")
    if (pending) {
      sessionStorage.removeItem("pending_product_modal")
      const frame = document.getElementById("product_modal_frame")
      if (frame) frame.src = pending
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKey)
  }

  // Intercepts clicks on "Menu", "+", "Order online" etc.
  // If the user hasn't picked a location yet, open the modal instead of
  // navigating/submitting. If they have, let the event through.
  gate(event) {
    if (this.locationResolvedValue) return
    event.preventDefault()
    event.stopPropagation()

    // Figure out where the user was trying to go so we can pass it through
    // the location flow as `return_to`.
    const target = event.currentTarget
    const href = target.getAttribute("href") || target.getAttribute("action")
    const opensProductModal = target.dataset.turboFrame === "product_modal_frame"

    let returnTo
    if (opensProductModal && href) {
      // Product modal links can't just be navigated to after location
      // resolution — they're turbo frame fragments, not standalone pages.
      // Stash the URL so the next page load can open the modal in its
      // frame, and send the user back to the page they clicked from.
      sessionStorage.setItem("pending_product_modal", href)
      returnTo = window.location.pathname + window.location.search
    } else if (href && href !== "#") {
      returnTo = href
    } else {
      returnTo = window.location.pathname + window.location.search
    }

    this.open(returnTo)
  }

  open(returnTo = null) {
    // Lazy-load the frame on first open (the layout omits src when location
    // is already resolved, so re-opens from the pill still fetch fresh content).
    if (this.hasFrameTarget) {
      const frame = this.frameTarget
      const currentSrc = frame.getAttribute("src")
      const dataSrc = frame.dataset.src
      const shouldSet = !currentSrc || currentSrc === "" || returnTo
      if (shouldSet && dataSrc) {
        const url = new URL(dataSrc, window.location.origin)
        if (returnTo) url.searchParams.set("return_to", returnTo)
        frame.setAttribute("src", url.pathname + url.search)
      }
    }
    this.openValue = true
  }

  close() {
    this.openValue = false
  }

  openValueChanged() {
    if (!this.hasPanelTarget || !this.hasBackdropTarget) return
    if (this.openValue) {
      this.backdropTarget.classList.remove("hidden")
      this.panelTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    } else {
      this.backdropTarget.classList.add("hidden")
      this.panelTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  panelTargetConnected(el) {
    if (this.openValue) {
      el.classList.remove("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    }
  }

  handleKey(e) {
    if (e.key === "Escape" && this.openValue) this.close()
  }
}
